import React, { useEffect, useState } from "react";
import axios from "axios";
import GLOBALS from "../../utils/Globals";
import { RotatingLines, Bars } from "react-loader-spinner";
import {
  Tabs,
  Tab,
  Container,
  FormLabel,
  Form,
  Button,
  Modal,

} from "react-bootstrap";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { findMultipleCommaFields } from "../../utils/helper";
import { v4 as uuidv4 } from "uuid";
import verifiedImage from '../../assets/img/gs1_verified.png';

const DynamicForm = ({ bucketName, objectName, email, thumbnails, apiUrl, testData, customer }) => {
  const [formData, setFormData] = useState({});
  const [jsonConfig, setJsonConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("case");
  const [activeSection, setActiveSection] = useState("case_information");
  const [changedFields, setChangedFields] = useState(new Set());
  const [originalData, setOriginalData] = useState({});
  const [jobId, setJobId] = useState(uuidv4());

  const [validationStates, setValidationStates] = useState({}); // Track GTIN validation states

  // Hi-Res Images upload state
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const [isProcessingWithOpenAI, setIsProcessingWithOpenAI] = useState(false);

  // Save state
  const [isSaving, setIsSaving] = useState(false);

  // Ingredients data from uploaded images
  const [ingredientsData, setIngredientsData] = useState([]);
  const [selectedIngredientsImage, setSelectedIngredientsImage] = useState(null);

  // Duplicate detection modal state
  const [duplicateModalVisible, setDuplicateModalVisible] = useState(false);
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [newDataForReplacement, setNewDataForReplacement] = useState(null);

  // Function to show duplicate modal
  const showDuplicateModal = (duplicateData, newData) => {
    setDuplicateInfo(duplicateData);
    setNewDataForReplacement(newData);
    setDuplicateModalVisible(true);
  };

  // Function to handle duplicate replacement
  const handleReplaceDuplicate = async () => {
    try {
      const baseURLReplaceDuplicate = process.env.REACT_APP_ENV === "PROD"
        ? GLOBALS.PROD.baseURLReplaceDuplicate || 'https://your-prod-domain.com/api/replace_duplicate_document'
        : GLOBALS.DEV.baseURLReplaceDuplicate || 'http://127.0.0.1:5050/api/replace_duplicate_document';

      const response = await axios.post(baseURLReplaceDuplicate, {
        existing_document_id: duplicateInfo.existing_document_id,
        new_data: newDataForReplacement
      });

      if (response.status === 200) {
        toast.success(`Document "${duplicateInfo.haribo_item_no}" has been successfully overwritten with new data!`, {
          position: "bottom-right",
          autoClose: 3000,
        });

        // Close modal
        setDuplicateModalVisible(false);
        setDuplicateInfo(null);
        setNewDataForReplacement(null);

        // Optionally redirect or refresh
        // window.location.reload();
      }
    } catch (error) {
      console.error('Error replacing duplicate:', error);
      toast.error('Failed to replace document. Please try again.', {
        position: "bottom-right",
        autoClose: 5000,
      });
    }
  };

  // Helper function to clean UPC/GTIN values by removing spaces
  const cleanUPCValue = (value) => {
    if (!value || typeof value !== 'string') return value;
    return value.replace(/\s+/g, ''); // Remove all whitespace characters
  };

  // Function to validate GTIN using the GS1 service
  const validateGTIN = async (gtin, type = 'case') => {
    if (!gtin || !gtin.trim()) {
      return {
        status: "not_validated",
        message: "No GTIN provided",
        timestamp: new Date().toISOString()
      };
    }

    try {
      const baseURLFetchGS1GTIN =
        process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLFetchGS1GTIN
          : GLOBALS.DEV.baseURLFetchGS1GTIN;

      const formDataGTIN = new FormData();
      formDataGTIN.append('gtin', gtin.trim());

      const gtinResponse = await axios.post(baseURLFetchGS1GTIN, formDataGTIN, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      console.log(`=== GTIN VALIDATION RESPONSE (${type}) ===`);
      console.log('GTIN:', gtin);
      console.log('Response Data:', gtinResponse.data);
      console.log('========================================');

      const responseData = gtinResponse.data;
      let hasValidationErrors = false;
      let errorMessage = '';

      if (responseData && responseData.products && Array.isArray(responseData.products)) {
        const firstProduct = responseData.products[0];

        if (firstProduct && firstProduct.validationErrors && Array.isArray(firstProduct.validationErrors)) {
          hasValidationErrors = true;
          const errors = firstProduct.validationErrors
            .flatMap(ve => ve.errors || [])
            .map(e => e.message)
            .join(', ');
          errorMessage = `GTIN validation failed: ${errors}`;
        } else if (firstProduct && firstProduct.gtinRecordStatus) {
          // Valid response
          return {
            status: "valid",
            message: "GTIN validated successfully",
            timestamp: new Date().toISOString()
          };
        } else {
          hasValidationErrors = true;
          errorMessage = 'Unexpected response structure from GS1 service';
        }
      } else {
        hasValidationErrors = true;
        errorMessage = 'Invalid response from GS1 service';
      }

      return {
        status: "invalid",
        message: errorMessage,
        timestamp: new Date().toISOString()
      };

    } catch (error) {
      console.error("GTIN validation error:", error);
      return {
        status: "error",
        message: `Validation error: ${error.message}`,
        timestamp: new Date().toISOString()
      };
    }
  };

  // Function to render validation badge
  const renderValidationBadge = (validationResult) => {
    if (!validationResult) return null;

    const { status, message } = validationResult;

    let badgeClass = '';
    let badgeText = '';

    switch (status) {
      case 'valid':
        badgeClass = 'badge bg-success ms-2';
        badgeText = 'Validated';
        break;
      case 'invalid':
        badgeClass = 'badge bg-danger ms-2';
        badgeText = 'Not Validated';
        break;
      case 'error':
        badgeClass = 'badge bg-warning ms-2';
        badgeText = 'Validation Error';
        break;
      case 'not_validated':
      default:
        badgeClass = 'badge bg-secondary ms-2';
        badgeText = 'Not Validated';
        break;
    }

    return (
      <span
        className={badgeClass}
        title={message}
        style={{ fontSize: '0.75em' }}
      >
        {badgeText}
      </span>
    );
  };






  


  // Field categorization mapping
  const fieldCategoryMapping = {
    microbiological_properties: [
      'total plate count',
      'total aerobic count',
      'aerobic plate count',
      'yeast',
      'mold',
      'yeast and mold',
      'yeast & mold',
      'coliform',
      'e. coli',
      'e.coli',
      'escherichia coli',
      'salmonella',
      'listeria',
      'listeria monocytogenes',
      'staphylococcus aureus',
      'enterobacteriaceae',
      'coliforms',
      'pathogen',
      'bacteria',
      'microbial',
      'cfu',
      'colony forming units',
      'plate count',
      'aerobic count',
      'yeast count',
      'mold count',
      'microbiological',
      'micro'
    ],
    product_claims_certifications: [
      'organic',
      'non-gmo',
      'non gmo',
      'gluten free',
      'gluten-free',
      'kosher',
      'halal',
      'fair trade',
      'usda organic',
      'certified',
      'certification',
      'claim',
      'natural',
      'artificial',
      'preservative free',
      'no artificial',
      'brc',
      'sqs',
      'haccp',
      'iso',
      'gmo',
      'non-dairy',
      'vegan',
      'vegetarian',
      'allergen free',
      'prop 65',
      'proposition 65',
      'ca prop',
      'warning',
      'no warning needed'
    ],
    physical_chemical_attributes: [
      'ph',
      'acidity',
      'moisture',
      'water activity',
      'aw',
      'brix',
      'density',
      'viscosity',
      'color',
      'colour',
      'texture',
      'hardness',
      'temperature',
      'solubility',
      'appearance',
      'odor',
      'odour',
      'taste',
      'flavor',
      'flavour'
    ],
    nutritional_information: [
      'calories',
      'protein',
      'fat',
      'carbohydrate',
      'carbs',
      'fiber',
      'fibre',
      'sugar',
      'sodium',
      'vitamin',
      'mineral',
      'calcium',
      'iron',
      'potassium',
      'cholesterol',
      'saturated fat',
      'trans fat',
      'serving size',
      'nutrition',
      'nutritional'
    ]
  };

  // Function to categorize a field based on its name
  const categorizeField = (fieldName) => {
    const lowerFieldName = fieldName.toLowerCase().trim();



    // PRIORITY 1: Microbiological fields - these should ALWAYS go to microbiological_properties
    // Check for exact matches first - these are CRITICAL and must be moved
    const exactMicrobiologicalFields = [
      'total plate count',
      'yeast and mold',
      'yeast & mold',
      'plate count',
      'aerobic plate count',
      'total aerobic count'
    ];

    if (exactMicrobiologicalFields.includes(lowerFieldName)) {
      console.log(`EXACT MICROBIOLOGICAL MATCH: "${fieldName}" -> microbiological_properties`);
      return 'microbiological_properties';
    }

    // Check for partial matches that contain key microbiological terms
    const microKeywords = ['plate count', 'cfu', 'colony forming', 'bacteria', 'microbial'];
    if (microKeywords.some(keyword => lowerFieldName.includes(keyword))) {
      console.log(`PARTIAL MICROBIOLOGICAL MATCH: "${fieldName}" contains microbiological keyword -> microbiological_properties`);
      return 'microbiological_properties';
    }

    // Special handling for yeast and mold - these can appear in various forms
    if (lowerFieldName.includes('yeast') || lowerFieldName.includes('mold')) {
      console.log(`YEAST/MOLD MATCH: "${fieldName}" -> microbiological_properties`);
      return 'microbiological_properties';
    }

    // PRIORITY 2: Product Claims/Certifications - check for these next
    const claimsKeywords = ['organic', 'non-gmo', 'non gmo', 'gluten free', 'gluten-free', 'kosher', 'halal', 'certified', 'certification', 'claim', 'natural', 'gmo', 'vegan', 'vegetarian', 'prop 65', 'proposition 65', 'ca prop', 'warning'];
    if (claimsKeywords.some(keyword => lowerFieldName.includes(keyword))) {
      console.log(`CLAIMS MATCH: "${fieldName}" -> product_claims_certifications`);
      return 'product_claims_certifications';
    }

    // Special handling for CA Prop 65 Warning field
    if (lowerFieldName.includes('prop 65') || lowerFieldName.includes('proposition 65') || lowerFieldName.includes('ca prop')) {
      console.log(`CA PROP 65 MATCH: "${fieldName}" -> product_claims_certifications`);
      return 'product_claims_certifications';
    }

    // PRIORITY 3: Physical/Chemical attributes
    const physicalKeywords = ['ph', 'moisture', 'water activity', 'aw', 'color', 'colour', 'texture', 'appearance', 'odor', 'odour', 'taste', 'flavor', 'flavour'];
    if (physicalKeywords.some(keyword => lowerFieldName.includes(keyword))) {
      console.log(`PHYSICAL MATCH: "${fieldName}" -> physical_chemical_attributes`);
      return 'physical_chemical_attributes';
    }

    // PRIORITY 4: Check for other categories using the mapping
    for (const [category, keywords] of Object.entries(fieldCategoryMapping)) {
      if (keywords.some(keyword => lowerFieldName.includes(keyword))) {
        console.log(`CATEGORY MATCH: "${fieldName}" -> ${category}`);
        return category;
      }
    }

    console.log(`NO MATCH: "${fieldName}" -> staying in current section`);
    return null; // Return null if no category match found
  };

  // Function to reorganize product fields into correct sections
  const reorganizeProductFields = (product) => {
    console.log('=== REORGANIZING PRODUCT FIELDS ===');
    console.log('Original product:', product);

    const reorganizedProduct = { ...product };
    const fieldsToMove = [];

    // Check all sections for misplaced fields (but be more conservative)
    Object.entries(product).forEach(([sectionKey, sectionData]) => {
      if (typeof sectionData === 'object' && sectionData !== null &&
          !['product_id', 'name', 'custom_fields'].includes(sectionKey)) {

        console.log(`Checking section: ${sectionKey}`, sectionData);

        Object.entries(sectionData).forEach(([fieldName, fieldValue]) => {
          const correctCategory = categorizeField(fieldName);
          const lowerFieldName = fieldName.toLowerCase().trim();

          console.log(`Field: "${fieldName}" -> Current: ${sectionKey}, Should be: ${correctCategory}`);

          // CRITICAL: Always move Total Plate Count and Yeast and Mold to microbiological_properties
          const criticalMicroFields = ['total plate count', 'yeast and mold', 'yeast & mold'];
          if (criticalMicroFields.includes(lowerFieldName) && sectionKey !== 'microbiological_properties') {
            fieldsToMove.push({
              fieldName,
              fieldValue,
              fromSection: sectionKey,
              toSection: 'microbiological_properties'
            });
            console.log(`CRITICAL MOVE: "${fieldName}" from ${sectionKey} to microbiological_properties`);
            return; // Skip other checks for these critical fields
          }

          // SPECIAL CASE: Handle "No Warning Needed" value that should be CA Prop 65 Warning
          if (fieldValue === "No Warning Needed" || lowerFieldName === "no warning needed" ||
              (typeof fieldValue === 'string' && fieldValue.toLowerCase().includes("no warning needed"))) {
            // This should be the CA Prop 65 Warning field
            fieldsToMove.push({
              fieldName: "CA Prop 65 Warning",
              fieldValue: "No Warning Needed",
              fromSection: sectionKey,
              toSection: 'product_claims_certifications'
            });
            console.log(`SPECIAL MOVE: Converting "${fieldName}" with value "${fieldValue}" to "CA Prop 65 Warning" in product_claims_certifications`);
            return;
          }

          // ADDITIONAL: Look for any field that might be CA Prop 65 related
          if (lowerFieldName.includes("no warning") ||
              (typeof fieldValue === 'string' && fieldValue.toLowerCase().includes("no warning"))) {
            fieldsToMove.push({
              fieldName: "CA Prop 65 Warning",
              fieldValue: fieldValue.includes("No Warning") ? fieldValue : "No Warning Needed",
              fromSection: sectionKey,
              toSection: 'product_claims_certifications'
            });
            console.log(`CA PROP 65 DETECTION: Converting "${fieldName}" with value "${fieldValue}" to "CA Prop 65 Warning"`);
            return;
          }

          // SPECIAL PROTECTION: Never move Country of Origin field
          if (fieldName === "Country of Origin") {
            console.log(`🔒 PROTECTING COUNTRY OF ORIGIN: Field will stay in ${sectionKey}`);
            return; // Skip any reorganization for this field
          }

          // Move other fields if they are clearly in the wrong section
          if (correctCategory && correctCategory !== sectionKey) {
            // Be more aggressive about moving microbiological fields
            if (correctCategory === 'microbiological_properties' ||
                (lowerFieldName.includes('plate count') || lowerFieldName.includes('yeast') || lowerFieldName.includes('mold'))) {
              fieldsToMove.push({
                fieldName,
                fieldValue,
                fromSection: sectionKey,
                toSection: correctCategory
              });
              console.log(`Will move "${fieldName}" from ${sectionKey} to ${correctCategory}`);
            }
            // For non-microbiological fields, only move if not already in a specialized section
            else if (!['microbiological_properties', 'product_claims_certifications', 'physical_chemical_attributes'].includes(sectionKey)) {
              fieldsToMove.push({
                fieldName,
                fieldValue,
                fromSection: sectionKey,
                toSection: correctCategory
              });
              console.log(`Will move "${fieldName}" from ${sectionKey} to ${correctCategory}`);
            }
          }
        });
      }
    });

    console.log('Fields to move:', fieldsToMove);

    // Move fields to correct sections
    fieldsToMove.forEach(({ fieldName, fieldValue, fromSection, toSection }) => {
      // Remove from original section
      if (reorganizedProduct[fromSection]) {
        delete reorganizedProduct[fromSection][fieldName];
        console.log(`Removed "${fieldName}" from ${fromSection}`);
      }

      // Add to correct section
      if (!reorganizedProduct[toSection]) {
        reorganizedProduct[toSection] = {};
      }
      reorganizedProduct[toSection][fieldName] = fieldValue;
      console.log(`Added "${fieldName}" to ${toSection}`);
    });

    // Ensure Microbiological Properties section has ONLY the required fields
    if (!reorganizedProduct.microbiological_properties) {
      reorganizedProduct.microbiological_properties = {};
    }

    // Clean up microbiological_properties to only have the two required fields
    const allowedMicroFields = ['Total Plate Count', 'Yeast and Mold'];
    const currentMicroFields = { ...reorganizedProduct.microbiological_properties };

    // Remove any fields that aren't the two required ones and try to recategorize them
    Object.keys(currentMicroFields).forEach(fieldName => {
      if (!allowedMicroFields.includes(fieldName)) {
        console.log(`Removing "${fieldName}" from microbiological_properties (not a required field)`);
        const fieldValue = currentMicroFields[fieldName];
        delete reorganizedProduct.microbiological_properties[fieldName];

        // Try to recategorize the field to a more appropriate section
        const correctCategory = categorizeField(fieldName);
        if (correctCategory && correctCategory !== 'microbiological_properties') {
          if (!reorganizedProduct[correctCategory]) {
            reorganizedProduct[correctCategory] = {};
          }
          reorganizedProduct[correctCategory][fieldName] = fieldValue;
          console.log(`Moved "${fieldName}" to ${correctCategory}`);
        } else {
          // Move to product_specific_fields as fallback
          if (!reorganizedProduct.product_specific_fields) {
            reorganizedProduct.product_specific_fields = {};
          }
          reorganizedProduct.product_specific_fields[fieldName] = fieldValue;
          console.log(`Moved "${fieldName}" to product_specific_fields`);
        }
      }
    });

    // Add required microbiological fields if they don't exist
    const requiredMicroFields = {
      'Total Plate Count': '',
      'Yeast and Mold': ''
    };

    Object.entries(requiredMicroFields).forEach(([fieldName, defaultValue]) => {
      if (!reorganizedProduct.microbiological_properties[fieldName]) {
        reorganizedProduct.microbiological_properties[fieldName] = defaultValue;
        console.log(`Added required field: "${fieldName}"`);
      }
    });

    // SAFEGUARD: Ensure Country of Origin field is preserved in product_information
    if (product.product_information && product.product_information["Country of Origin"]) {
      if (!reorganizedProduct.product_information) {
        reorganizedProduct.product_information = {};
      }
      reorganizedProduct.product_information["Country of Origin"] = product.product_information["Country of Origin"];
      console.log(`🔒 SAFEGUARD: Ensured Country of Origin is in product_information: "${product.product_information["Country of Origin"]}"`);
    }

    // SAFEGUARD: Ensure physical_chemical_attributes section is preserved
    if (product.physical_chemical_attributes) {
      if (!reorganizedProduct.physical_chemical_attributes) {
        reorganizedProduct.physical_chemical_attributes = {};
      }
      // Preserve all fields in physical_chemical_attributes
      Object.entries(product.physical_chemical_attributes).forEach(([fieldName, fieldValue]) => {
        reorganizedProduct.physical_chemical_attributes[fieldName] = fieldValue;
      });
      console.log(`🔒 SAFEGUARD: Ensured physical_chemical_attributes is preserved:`, reorganizedProduct.physical_chemical_attributes);
    }

    // SAFEGUARD: Ensure microbiological_properties section is preserved
    if (product.microbiological_properties) {
      if (!reorganizedProduct.microbiological_properties) {
        reorganizedProduct.microbiological_properties = {};
      }
      // Preserve all fields in microbiological_properties
      Object.entries(product.microbiological_properties).forEach(([fieldName, fieldValue]) => {
        reorganizedProduct.microbiological_properties[fieldName] = fieldValue;
      });
    }

    // SAFEGUARD: Ensure barcode_images section is preserved
    if (product.barcode_images) {
      reorganizedProduct.barcode_images = product.barcode_images;
      console.log(`🔒 SAFEGUARD: Ensured barcode_images is preserved:`, reorganizedProduct.barcode_images);
      console.log(`🔒 SAFEGUARD: Ensured microbiological_properties is preserved:`, reorganizedProduct.microbiological_properties);
    }

    console.log('Reorganized product:', reorganizedProduct);

    // Final check for physical_chemical_attributes
    if (reorganizedProduct.physical_chemical_attributes) {
      console.log(`✅ FINAL CHECK: physical_chemical_attributes preserved with ${Object.keys(reorganizedProduct.physical_chemical_attributes).length} fields`);
    } else {
      console.log(`❌ FINAL CHECK: physical_chemical_attributes is missing from reorganized product`);
    }

    // Final check for microbiological_properties
    if (reorganizedProduct.microbiological_properties) {
      console.log(`✅ FINAL CHECK: microbiological_properties preserved with ${Object.keys(reorganizedProduct.microbiological_properties).length} fields`);
    } else {
      console.log(`❌ FINAL CHECK: microbiological_properties is missing from reorganized product`);
    }

    console.log('=== END REORGANIZATION ===');

    return reorganizedProduct;
  };

  // Function to reorganize case fields into correct sections
  const reorganizeCaseFields = (caseData) => {
    console.log('=== REORGANIZING CASE FIELDS ===');
    console.log('Original case data:', caseData);

    if (!caseData || typeof caseData !== 'object') {
      return caseData;
    }

    const reorganizedCase = { ...caseData };
    const fieldsToMove = [];

    // Check all case sections for misplaced fields
    Object.entries(caseData).forEach(([sectionKey, sectionData]) => {
      if (typeof sectionData === 'object' && sectionData !== null) {
        console.log(`Checking case section: ${sectionKey}`, sectionData);

        Object.entries(sectionData).forEach(([fieldName, fieldValue]) => {
          const lowerFieldName = fieldName.toLowerCase().trim();

          // CRITICAL: Always move Total Plate Count and Yeast and Mold to microbiological_properties
          const criticalMicroFields = ['total plate count', 'yeast and mold', 'yeast & mold'];
          if (criticalMicroFields.includes(lowerFieldName) && sectionKey !== 'microbiological_properties') {
            fieldsToMove.push({
              fieldName,
              fieldValue,
              fromSection: sectionKey,
              toSection: 'microbiological_properties'
            });
            console.log(`CRITICAL CASE MOVE: "${fieldName}" from ${sectionKey} to microbiological_properties`);
          }

          // SPECIAL CASE: Handle "No Warning Needed" value that should be CA Prop 65 Warning
          if (fieldValue === "No Warning Needed" || lowerFieldName === "no warning needed" ||
              (typeof fieldValue === 'string' && fieldValue.toLowerCase().includes("no warning needed"))) {
            // This should be the CA Prop 65 Warning field
            fieldsToMove.push({
              fieldName: "CA Prop 65 Warning",
              fieldValue: "No Warning Needed",
              fromSection: sectionKey,
              toSection: 'product_claims_certifications'
            });
            console.log(`SPECIAL CASE MOVE: Converting "${fieldName}" with value "${fieldValue}" to "CA Prop 65 Warning" in product_claims_certifications`);
          }

          // ADDITIONAL: Look for any field that might be CA Prop 65 related
          if (lowerFieldName.includes("no warning") ||
              (typeof fieldValue === 'string' && fieldValue.toLowerCase().includes("no warning"))) {
            fieldsToMove.push({
              fieldName: "CA Prop 65 Warning",
              fieldValue: fieldValue.includes("No Warning") ? fieldValue : "No Warning Needed",
              fromSection: sectionKey,
              toSection: 'product_claims_certifications'
            });
            console.log(`CA PROP 65 CASE DETECTION: Converting "${fieldName}" with value "${fieldValue}" to "CA Prop 65 Warning"`);
          }
        });
      }
    });

    // Move fields to correct sections
    fieldsToMove.forEach(({ fieldName, fieldValue, fromSection, toSection }) => {
      // Remove from original section
      if (reorganizedCase[fromSection]) {
        delete reorganizedCase[fromSection][fieldName];
        console.log(`Removed "${fieldName}" from case ${fromSection}`);
      }

      // Add to correct section
      if (!reorganizedCase[toSection]) {
        reorganizedCase[toSection] = {};
      }
      reorganizedCase[toSection][fieldName] = fieldValue;
      console.log(`Added "${fieldName}" to case ${toSection}`);
    });

    // Ensure microbiological_properties section exists and has required fields
    if (!reorganizedCase.microbiological_properties) {
      reorganizedCase.microbiological_properties = {};
    }

    // Add required microbiological fields if they don't exist
    const requiredMicroFields = {
      'Total Plate Count': '',
      'Yeast and Mold': ''
    };

    Object.entries(requiredMicroFields).forEach(([fieldName, defaultValue]) => {
      if (!reorganizedCase.microbiological_properties[fieldName]) {
        reorganizedCase.microbiological_properties[fieldName] = defaultValue;
        console.log(`Added required case field: "${fieldName}"`);
      }
    });

    console.log('Reorganized case data:', reorganizedCase);
    console.log('=== END CASE REORGANIZATION ===');

    return reorganizedCase;
  };

  // Function to ensure CA Prop 65 Warning field is properly set
  const ensureCAProp65Warning = (data) => {
    console.log('=== ENSURING CA PROP 65 WARNING ===');

    if (!data) return data;

    const updatedData = { ...data };

    // Check case data
    if (updatedData.case) {
      let foundCAProp65 = false;

      // Look through all case sections for CA Prop 65 related data
      Object.entries(updatedData.case).forEach(([sectionKey, sectionData]) => {
        if (typeof sectionData === 'object' && sectionData !== null) {
          Object.entries(sectionData).forEach(([fieldName, fieldValue]) => {
            const lowerFieldName = fieldName.toLowerCase();
            const lowerFieldValue = typeof fieldValue === 'string' ? fieldValue.toLowerCase() : '';

            // Look for "No Warning Needed" or similar values
            if (lowerFieldValue.includes('no warning') || lowerFieldName.includes('no warning')) {
              console.log(`Found CA Prop 65 data in case: ${fieldName} = ${fieldValue}`);

              // Ensure product_claims_certifications section exists
              if (!updatedData.case.product_claims_certifications) {
                updatedData.case.product_claims_certifications = {};
              }

              // Set the CA Prop 65 Warning field
              updatedData.case.product_claims_certifications['CA Prop 65 Warning'] = 'No Warning Needed';
              foundCAProp65 = true;

              // Remove the original field if it's not already named correctly
              if (fieldName !== 'CA Prop 65 Warning') {
                delete updatedData.case[sectionKey][fieldName];
                console.log(`Removed original field: ${fieldName} from ${sectionKey}`);
              }
            }
          });
        }
      });

      // If we didn't find it, add it as a default
      if (!foundCAProp65) {
        if (!updatedData.case.product_claims_certifications) {
          updatedData.case.product_claims_certifications = {};
        }
        updatedData.case.product_claims_certifications['CA Prop 65 Warning'] = 'No Warning Needed';
        console.log('Added default CA Prop 65 Warning to case');
      }
    }

    // Check product data
    if (updatedData.products && Array.isArray(updatedData.products)) {
      updatedData.products = updatedData.products.map(product => {
        let foundCAProp65 = false;
        const updatedProduct = { ...product };

        // Look through all product sections for CA Prop 65 related data
        Object.entries(updatedProduct).forEach(([sectionKey, sectionData]) => {
          if (typeof sectionData === 'object' && sectionData !== null &&
              !['product_id', 'name', 'custom_fields'].includes(sectionKey)) {
            Object.entries(sectionData).forEach(([fieldName, fieldValue]) => {
              const lowerFieldName = fieldName.toLowerCase();
              const lowerFieldValue = typeof fieldValue === 'string' ? fieldValue.toLowerCase() : '';

              // Look for "No Warning Needed" or similar values
              if (lowerFieldValue.includes('no warning') || lowerFieldName.includes('no warning')) {
                console.log(`Found CA Prop 65 data in product: ${fieldName} = ${fieldValue}`);

                // Ensure product_claims_certifications section exists
                if (!updatedProduct.product_claims_certifications) {
                  updatedProduct.product_claims_certifications = {};
                }

                // Set the CA Prop 65 Warning field
                updatedProduct.product_claims_certifications['CA Prop 65 Warning'] = 'No Warning Needed';
                foundCAProp65 = true;

                // Remove the original field if it's not already named correctly
                if (fieldName !== 'CA Prop 65 Warning') {
                  delete updatedProduct[sectionKey][fieldName];
                  console.log(`Removed original field: ${fieldName} from ${sectionKey}`);
                }
              }
            });
          }
        });

        // If we didn't find it, add it as a default
        if (!foundCAProp65) {
          if (!updatedProduct.product_claims_certifications) {
            updatedProduct.product_claims_certifications = {};
          }
          updatedProduct.product_claims_certifications['CA Prop 65 Warning'] = 'No Warning Needed';
          console.log('Added default CA Prop 65 Warning to product');
        }

        return updatedProduct;
      });
    }

    console.log('=== END CA PROP 65 ENSURING ===');
    return updatedData;
  };





  // Helper function to check if a product has any changes
  const hasProductChanges = (productIndex) => {
    return Array.from(changedFields).some((fieldId) =>
      fieldId.startsWith(`${productIndex}-`)
    );
  };

  // Helper function to check if a section has any changes
  const hasSectionChanges = (productIndex, sectionKey) => {
    return Array.from(changedFields).some((fieldId) =>
      fieldId.startsWith(`${productIndex}-${sectionKey}`)
    );
  };

  // Custom styles for tabs
  const getProductTabStyle = (productIndex) => {
    return hasProductChanges(productIndex)
      ? {
          backgroundColor: "#fff3cd",
          borderColor: "#ffc107",
          color: "#000",
        }
      : {};
  };

  const getSectionTabStyle = (productIndex, sectionKey) => {
    return hasSectionChanges(productIndex, sectionKey)
      ? {
          backgroundColor: "#fff3cd",
          borderColor: "#ffc107",
          color: "#000",
        }
      : {};
  };



  const baseURLOpenAIGetDataPDF =
    process.env.REACT_APP_ENV == "PROD"
      ? GLOBALS.PROD.baseURLOpenAIGetDataPDF
      : GLOBALS.DEV.baseURLOpenAIGetDataPDF;

  const baseURLSavePDFToMongo =
    process.env.REACT_APP_ENV == "PROD"
      ? GLOBALS.PROD.baseURLSavePDFToMongo
      : GLOBALS.DEV.baseURLSavePDFToMongo;

  useEffect(() => {
    const fetchData = async () => {
      // If test data is provided, use it instead of fetching from API
      if (testData) {
        console.log('=== USING TEST DATA ===');
        console.log('Test data:', testData);

        setJsonConfig(testData);
        setFormData(testData);
        setOriginalData(JSON.parse(JSON.stringify(testData))); // Deep copy for comparison
        setActiveTab("case");
        setLoading(false);
        return;
      }

      try {
        const object_name = email + "/tmp/" + objectName;
        const bucket_name = GLOBALS.AWS.S3_BUCKET_NAME;

        const formData = new FormData();
        formData.append("object_name", object_name);
        formData.append("bucket_name", bucket_name);
        formData.append("email", email);
        formData.append("customer", customer || '');

        const response = await axios.post(baseURLOpenAIGetDataPDF, formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });

        console.log('=== baseURLOpenAIGetDataPDF Response ===');
        console.log('Response status:', response.status);
        console.log('Response data:', response.data);
        console.log('Response headers:', response.headers);
        console.log('=======================================');

        // Determine product type based on sub_header
        const apiResponseData = response.data;
        let productType = 'Case'; // Default type

        if (apiResponseData.header && apiResponseData.header.sub_header) {
          const subHeader = apiResponseData.header.sub_header.toLowerCase();
          if (subHeader.includes('pdq')) {
            productType = 'Display Shipper';
          }
        }

        console.log('Determined product type:', productType, 'based on sub_header:', apiResponseData.header?.sub_header);

        // Handle new JSON structure with case and products
        const responseData = apiResponseData;
        console.log('Processing new JSON structure:', responseData);

        // Debug barcode data
        if (responseData.case && responseData.case.barcode_images) {
          console.log('🔍 BARCODE DEBUG: Case barcode images found:', responseData.case.barcode_images);
        }
        if (responseData.products) {
          responseData.products.forEach((product, index) => {
            if (product.barcode_images) {
              console.log(`🔍 BARCODE DEBUG: Product ${index + 1} barcode images found:`, product.barcode_images);
            }
          });
        }

        let processedData = {};

        if (responseData.frontend_format) {
          // Use frontend_format if available
          processedData = { ...responseData.frontend_format };
        } else {
          // Fallback to direct structure
          processedData = { ...responseData };
        }

        // Ensure we have the case section and add product type
        if (responseData.case) {
          let caseData = {
            ...responseData.case,
            case_information: {
              ...responseData.case.case_information,
              'Product Type': productType
            },
            // Ensure these sections exist in case
            microbiological_properties: {
              'Total Plate Count': '',
              'Yeast and Mold': '',
              ...responseData.case.microbiological_properties
            },
            product_claims_certifications: {
              ...responseData.case.product_claims_certifications
            },
            physical_chemical_attributes: {
              ...responseData.case.physical_chemical_attributes
            }
          };

          // Preserve barcode images if they exist
          if (responseData.case.barcode_images) {
            caseData.barcode_images = responseData.case.barcode_images;
            console.log('Preserved case barcode images:', caseData.barcode_images);
          }

          // Clean Case UPC by removing spaces
          if (caseData.case_information && caseData.case_information["Case UPC"]) {
            caseData.case_information["Case UPC"] = cleanUPCValue(caseData.case_information["Case UPC"]);
            console.log('Cleaned Case UPC:', caseData.case_information["Case UPC"]);
          }

          // Reorganize case fields to ensure they're in the right sections
          processedData.case = reorganizeCaseFields(caseData);
        } else {
          // Create case section with product type if it doesn't exist
          processedData.case = {
            case_information: {
              'Product Type': productType
            },
            microbiological_properties: {
              'Total Plate Count': '',
              'Yeast and Mold': ''
            },
            product_claims_certifications: {},
            physical_chemical_attributes: {}
          };
        }

        // Process products array and add UUIDs
        if (responseData.products && Array.isArray(responseData.products)) {
          processedData.products = responseData.products.map(product => {
            // Keep product_information as-is from the JSON
            let fixedProductInfo = { ...product.product_information };

            // Clean Retail Pkg. UPC by removing spaces
            if (fixedProductInfo["Retail Pkg. UPC"]) {
              fixedProductInfo["Retail Pkg. UPC"] = cleanUPCValue(fixedProductInfo["Retail Pkg. UPC"]);
              console.log('Cleaned Retail Pkg. UPC:', fixedProductInfo["Retail Pkg. UPC"]);
            }

            const productWithId = {
              ...product,
              product_id: product.product_id || uuidv4(),
              // Add Product Type to product_information
              product_information: {
                ...fixedProductInfo,
                'Product Type': 'Each'
              }
            };

            // Clean up Case UPC if it exists in case_information (legacy support)
            if (productWithId.case_information && productWithId.case_information["Case UPC"]) {
              productWithId.case_information["Case UPC"] = cleanUPCValue(productWithId.case_information["Case UPC"]);
              console.log('Cleaned Case UPC in product:', productWithId.case_information["Case UPC"]);
            }

            // Reorganize fields into correct sections
            const reorganizedProduct = reorganizeProductFields(productWithId);

            return reorganizedProduct;
          });

          // Add product_ids to job_details
          if (!processedData.job_details) {
            processedData.job_details = {};
          }
          processedData.job_details.product_ids = processedData.products.map(p => p.product_id);
        }

        // Set number of products
        if (responseData.number_of_products) {
          processedData.how_many_products_are_on_this_page = responseData.number_of_products.toString();
        }

        // Ensure CA Prop 65 Warning field is properly set
        processedData = ensureCAProp65Warning(processedData);

        console.log('Processed form data:', processedData);

        // Debug product information structure
        if (processedData.products && processedData.products.length > 0) {
          console.log('=== PRODUCT INFORMATION DEBUG ===');
          console.log('First product product_information:', processedData.products[0].product_information);
          console.log('Product information keys:', Object.keys(processedData.products[0].product_information || {}));

          // Log each field name and value separately
          Object.entries(processedData.products[0].product_information || {}).forEach(([key, value]) => {
            console.log(`Field: "${key}" | Value: "${value}"`);
          });
          console.log('================================');
        }

        setJsonConfig(processedData);
        setFormData(processedData);
        setOriginalData(JSON.parse(JSON.stringify(processedData))); // Deep copy for comparison

        // Set default tab to Case Information when new data is loaded
        setActiveTab("case");
        setLoading(false);
      } catch (error) {
        console.error("Error fetching JSON data:", error);
        setError(error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Handle field change with tracking
  const handleChange = async (productIndex, section, fieldName, value) => {
    const fieldId = `${productIndex}-${section}-${fieldName}`;

    // Debug logging for Case UPC changes
    if (fieldName === "Case UPC") {
      console.log('=== CASE UPC CHANGE DETECTED ===');
      console.log('Product Index:', productIndex);
      console.log('Section:', section);
      console.log('Field Name:', fieldName);
      console.log('New Value:', value);
      console.log('Field ID:', fieldId);
      console.log('================================');
    }

    // Check if the value is different from the original
    const originalValue =
      originalData.products[productIndex][section][fieldName];
    if (value !== originalValue) {
      setChangedFields((prev) => new Set([...prev, fieldId]));
    } else {
      setChangedFields((prev) => {
        const newSet = new Set([...prev]);
        newSet.delete(fieldId);
        return newSet;
      });
    }

    setFormData((prevData) => {
      const newData = { ...prevData };
      newData.products[productIndex][section][fieldName] = value;

      // Debug logging for Case UPC form data update
      if (fieldName === "Case UPC") {
        console.log('=== FORM DATA UPDATED ===');
        console.log('Updated Case UPC in form data:', newData.products[productIndex][section][fieldName]);
        console.log('Full case_information:', newData.products[productIndex][section]);
        console.log('========================');
      }

      return newData;
    });

    // Real-time GTIN validation for Case UPC field in Case Information tab
    if (fieldName === "Case UPC" && section === "case_information" && value && value.trim()) {
      console.log('=== TRIGGERING REAL-TIME GTIN VALIDATION ===');
      console.log('Validating Case UPC:', value);

      try {
        const baseURLFetchGS1GTIN =
          process.env.REACT_APP_ENV === "PROD"
            ? GLOBALS.PROD.baseURLFetchGS1GTIN
            : GLOBALS.DEV.baseURLFetchGS1GTIN;

        const formDataGTIN = new FormData();
        formDataGTIN.append('gtin', value.trim());

        const gtinResponse = await axios.post(baseURLFetchGS1GTIN, formDataGTIN, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });

        // Console log the return data from baseURLFetchGS1GTIN
        console.log('=== REAL-TIME GTIN VALIDATION RESPONSE ===');
        console.log('Case UPC:', value);
        console.log('Response Data:', gtinResponse.data);
        console.log('==========================================');

        // Check if response has the expected structure and look for validation errors
        const responseData = gtinResponse.data;
        let hasValidationErrors = false;
        let errorMessage = '';
        let validationStatus = {
          status: "not_validated",
          message: "No Case UPC provided",
          timestamp: new Date().toISOString()
        };

        if (responseData && responseData.products && Array.isArray(responseData.products)) {
          const firstProduct = responseData.products[0];

          // Check for validation errors in the GS1 response structure
          if (firstProduct && firstProduct.validationErrors && Array.isArray(firstProduct.validationErrors)) {
            hasValidationErrors = true;

            // Extract error message from validation errors
            const errors = firstProduct.validationErrors
              .flatMap(ve => ve.errors || [])
              .map(e => e.message)
              .join(', ');

            errorMessage = `GTIN validation failed: ${errors}`;
            console.log('Real-time GTIN Validation FAILED - Validation errors found');
            console.log('Validation errors:', firstProduct.validationErrors);

            validationStatus = {
              status: "invalid",
              message: errorMessage,
              timestamp: new Date().toISOString()
            };
          } else if (firstProduct && firstProduct.gtinRecordStatus) {
            // Valid response with GTIN record status
            console.log('Real-time GTIN Validation SUCCESSFUL');
            console.log('GTIN Record Status:', firstProduct.gtinRecordStatus);

            validationStatus = {
              status: "valid",
              message: "Case UPC validated successfully",
              timestamp: new Date().toISOString()
            };
          } else {
            // Unexpected response structure
            hasValidationErrors = true;
            errorMessage = 'Unexpected response structure from GS1 service';
            console.log('Real-time GTIN Validation FAILED - Unexpected response structure');

            validationStatus = {
              status: "invalid",
              message: errorMessage,
              timestamp: new Date().toISOString()
            };
          }
        } else {
          // No products array or invalid response
          hasValidationErrors = true;
          errorMessage = 'Invalid response from GS1 service';
          console.log('Real-time GTIN Validation FAILED - No products array in response');

          validationStatus = {
            status: "invalid",
            message: errorMessage,
            timestamp: new Date().toISOString()
          };
        }

        // Update the validation status in form data with simple flag
        setFormData((prevData) => {
          const newData = { ...prevData };
          if (!newData.products[productIndex].validation) {
            newData.products[productIndex].validation = {};
          }
          // Set simple True/False flag based on validation result
          newData.products[productIndex].validation.gtin_valid = validationStatus?.status === 'valid';
          return newData;
        });

        // Show toast notification for validation result
        if (hasValidationErrors) {
          toast.warning(`Invalid Case UPC: ${value} - ${errorMessage}`, {
            position: "top-right",
            autoClose: 3000,
          });
        } else {
          toast.success(`Case UPC validated successfully: ${value}`, {
            position: "top-right",
            autoClose: 3000,
          });
        }

      } catch (gtinError) {
        console.error("Real-time GTIN validation error:", gtinError);

        // Update validation status with error - set to false for errors
        setFormData((prevData) => {
          const newData = { ...prevData };
          if (!newData.products[productIndex].validation) {
            newData.products[productIndex].validation = {};
          }
          // Set to false when there's an error
          newData.products[productIndex].validation.gtin_valid = false;
          return newData;
        });

        toast.error(`Could not validate Case UPC: ${value}`, {
          position: "top-right",
          autoClose: 3000,
        });
      }
    }
  };

  // Function to clean up empty sections from products before saving
  const cleanupEmptySections = (product) => {
    const cleanedProduct = { ...product };
    const removedSections = [];

    console.log(`🧹 CLEANUP START for product: ${product.name || 'Unknown'}`);
    console.log(`🧹 Original sections:`, Object.keys(product));

    // List of sections that should be removed if they are empty
    const sectionsToCheck = [
      'nutritional_information',
      'ingredients',
      'allergens',
      'product_specific_fields',
      'pallet_information',
      'shipping_information',
      'logistics_information',
      'storage_handling',
      'shelf_life',
      'preparation_instructions',
      'serving_information',
      'labeling_information',
      'regulatory_compliance',
      'quality_specifications',
      'traceability_information',
      'lot_batch_information'
    ];

    // Always keep these essential sections even if they have data
    // These sections should NEVER be removed during cleanup
    const essentialSections = [
      'product_information',
      'microbiological_properties',
      'physical_chemical_attributes',
      'product_claims_certifications',
      'validation'
    ];

    sectionsToCheck.forEach(sectionKey => {
      // Skip essential sections - never remove them even if empty
      if (essentialSections.includes(sectionKey)) {
        console.log(`🔒 SKIPPING ESSENTIAL SECTION: ${sectionKey} (never removed)`);
        return;
      }

      const section = cleanedProduct[sectionKey];

      // Special debugging for physical_chemical_attributes
      if (sectionKey === 'physical_chemical_attributes') {
        console.log(`🔍 PHYSICAL_CHEMICAL_ATTRIBUTES CLEANUP CHECK:`);
        console.log(`  - Section exists:`, !!section);
        console.log(`  - Section content:`, section);
        console.log(`  - Is essential:`, essentialSections.includes(sectionKey));
        console.log(`  - Keys count:`, section ? Object.keys(section).length : 0);
      }

      // Check if section is empty (null, undefined, empty object, or object with only empty values)
      if (!section ||
          (typeof section === 'object' && Object.keys(section).length === 0) ||
          (typeof section === 'object' && Object.values(section).every(value =>
            value === '' || value === null || value === undefined
          ))) {
        delete cleanedProduct[sectionKey];
        removedSections.push(sectionKey);

        if (sectionKey === 'physical_chemical_attributes') {
          console.log(`⚠️ WARNING: physical_chemical_attributes was removed during cleanup!`);
        }
      }
    });

    if (removedSections.length > 0) {
      console.log(`🧹 CLEANUP: Removed ${removedSections.length} empty sections:`, removedSections);
    } else {
      console.log(`🧹 CLEANUP: No empty sections to remove`);
    }

    return cleanedProduct;
  };

  const sections = [
    { key: "product_information", label: "Product Information" },
    { key: "nutritional_information", label: "Nutritional Information" },
    { key: "ingredients", label: "Ingredients" },
    { key: "allergens", label: "Allergens" },
    { key: "microbiological_properties", label: "Microbiological Properties" },
    {
      key: "physical_chemical_attributes",
      label: "Physical & Chemical Attributes",
    },
    {
      key: "product_claims_certifications",
      label: "Product Claims & Certifications",
    },
    { key: "product_specific_fields", label: "Product Specific Fields" },
    { key: "pallet_information", label: "Pallet Information" },
    { key: "shipping_information", label: "Shipping Information" },
    { key: "logistics_information", label: "Logistics Information" },
    { key: "storage_handling", label: "Storage & Handling" },
    { key: "shelf_life", label: "Shelf Life" },
    { key: "preparation_instructions", label: "Preparation Instructions" },
    { key: "serving_information", label: "Serving Information" },
    { key: "labeling_information", label: "Labeling Information" },
    { key: "regulatory_compliance", label: "Regulatory Compliance" },
    { key: "quality_specifications", label: "Quality Specifications" },
    { key: "traceability_information", label: "Traceability Information" },
    { key: "lot_batch_information", label: "Lot & Batch Information" },
    { key: "validation", label: "Validation" },
  ];

  const saveToDatabase = async () => {
    console.log("🚀 SAVE TO DATABASE FUNCTION CALLED - SHOULD ONLY CREATE ONE DOCUMENT");

    // Set saving state to show spinner
    setIsSaving(true);

      // Log ingredients data status
      const selectedIngredientsData = getSelectedIngredientsData();
      if (selectedIngredientsData) {
        console.log("🧪 INGREDIENTS DATA WILL BE INCLUDED IN SAVE:");
        console.log("  - Image Index:", selectedIngredientsData.imageIndex);
        console.log("  - Image URL:", selectedIngredientsData.imageUrl);
        console.log("  - Ingredients Array:", selectedIngredientsData.ingredients);
        console.log("  - Ingredients Count:", selectedIngredientsData.ingredients?.length || 0);
        console.log("  - Allergens Array:", selectedIngredientsData.allergens);
        console.log("  - Allergens Count:", selectedIngredientsData.allergens?.length || 0);
        console.log("  - Has Nutrition:", !!selectedIngredientsData.nutrition);
      } else {
        console.log("ℹ️ No ingredients data selected - saving without ingredients");
      }

    try {
      // Get the current active product index
      const currentProductIndex = activeTab;

      console.log('=== SAVE DEBUG ===');
      console.log('formData:', formData);
      console.log('formData.products:', formData.products);
      console.log('formData.products type:', typeof formData.products);
      console.log('formData.products isArray:', Array.isArray(formData.products));

      // Make sure formData.products exists and has the correct length
      if (!formData.products || !Array.isArray(formData.products) || formData.products.length === 0) {
        console.warn("No products found or invalid products data structure");
        console.log("Available formData keys:", Object.keys(formData));

        // If no products, just save case information
        if (!formData.products || !Array.isArray(formData.products)) {
          toast.warning("No products found to save. Only case information will be saved.", {
            position: "bottom-right",
            autoClose: 5000,
          });
        }
      }

      // Create a copy of the products array (handle empty case)
      const updatedProducts = formData.products && Array.isArray(formData.products) ? [...formData.products] : [];

      console.log('updatedProducts:', updatedProducts);
      console.log('updatedProducts.length:', updatedProducts.length);

      // Add selected ingredients data to products if available
      const selectedIngredientsData = getSelectedIngredientsData();
      if (selectedIngredientsData && updatedProducts.length > 0) {
        console.log('🧪 Adding selected ingredients data to products...');
        console.log('Selected ingredients data:', selectedIngredientsData);

        // Add ingredients data to all products (or you could specify which product)
        updatedProducts.forEach((product, index) => {
          // Initialize ingredients section if it doesn't exist
          if (!product.ingredients) {
            product.ingredients = {};
          }
          if (!product.allergens) {
            product.allergens = {};
          }
          if (!product.nutritional_information) {
            product.nutritional_information = {};
          }

          // Add the extracted ingredients data as an array
          product.ingredients = {
            ...product.ingredients,
            ingredients_list: selectedIngredientsData.ingredients // This is now an array
          };

          // Add allergen information as an array
          if (selectedIngredientsData.allergens && selectedIngredientsData.allergens.length > 0) {
            product.allergens = {
              ...product.allergens,
              allergen_information: selectedIngredientsData.allergens // This is now an array
            };
          }

          // Add nutritional information
          if (selectedIngredientsData.nutrition) {
            product.nutritional_information = {
              ...product.nutritional_information,
              nutrition_facts: selectedIngredientsData.nutrition
            };
          }

          console.log(`✅ Added ingredients data to product ${index + 1}:`);
          console.log('  - Ingredients:', product.ingredients);
          if (product.allergens) console.log('  - Allergens:', product.allergens);
          if (product.nutritional_information) console.log('  - Nutritional Info:', product.nutritional_information);
        });
      }

      console.log('==================');

      // Validate Case UPC from case information
      let caseValidation = null;
      if (formData.case && formData.case.case_information && formData.case.case_information["Case UPC"]) {
        const caseUPC = formData.case.case_information["Case UPC"];
        console.log('=== VALIDATING CASE UPC ===');
        console.log('Case UPC:', caseUPC);

        caseValidation = await validateGTIN(caseUPC, 'case');
        console.log('Case validation result:', caseValidation);

        // Update validation states for UI
        setValidationStates(prev => ({
          ...prev,
          case_upc: caseValidation
        }));

        // Add simple validation flag to case data
        if (!formData.case.validation) {
          formData.case.validation = {};
        }
        // Set simple True/False flag based on validation result
        formData.case.validation.gtin_valid = caseValidation?.status === 'valid';
      }

      // Validate GTIN/UPC for all products
      for (let i = 0; i < updatedProducts.length; i++) {
        const product = updatedProducts[i];

        // Initialize validation section if it doesn't exist
        if (!product.validation) {
          product.validation = {};
        }

        // Look for Retail Pkg. UPC field in product information
        const retailPkgUPC = product.product_information?.["Retail Pkg. UPC"];

        // Debug logging for validation
        console.log(`=== VALIDATION FOR PRODUCT ${i + 1} ===`);
        console.log('Product object:', product);
        console.log('Product information:', product.product_information);
        console.log('Retail Pkg. UPC value:', retailPkgUPC);
        console.log('Retail Pkg. UPC type:', typeof retailPkgUPC);
        console.log('Retail Pkg. UPC length:', retailPkgUPC?.length);
        console.log('=====================================');

        // Default validation status - simple True/False flag
        product.validation.gtin_valid = false;

        // Validate Retail Pkg. UPC if it exists
        if (retailPkgUPC && retailPkgUPC.trim()) {
          console.log(`Validating Retail Pkg. UPC for Product ${i + 1}:`, retailPkgUPC);

          const productValidation = await validateGTIN(retailPkgUPC, 'product');

          // Set simple True/False flag based on validation result
          product.validation.gtin_valid = productValidation?.status === 'valid';

          // Update validation states for UI (keep detailed for UI display)
          setValidationStates(prev => ({
            ...prev,
            [`product_${i}_retail_pkg_upc`]: productValidation
          }));

          console.log(`Product ${i + 1} validation result:`, productValidation);
          console.log(`Product ${i + 1} gtin_valid flag:`, product.validation.gtin_valid);
        }
      }

      // Prepare all cleaned products for inclusion in the case document
      const allCleanedProducts = [];
      let savedCase = null;

      console.log('=== PREPARING PRODUCTS FOR COMBINED DOCUMENT ===');
      console.log('Number of products to process:', updatedProducts.length);

      for (let i = 0; i < updatedProducts.length; i++) {
        const product = updatedProducts[i];
        console.log(`Processing product ${i + 1}:`, product);

        // DEBUG: Check where physical_chemical_attributes exists in formData
        console.log(`🔍 CASE DATA STRUCTURE CHECK:`);
        console.log(`  - formData.case exists:`, !!formData.case);
        console.log(`  - formData.case.physical_chemical_attributes:`, formData.case?.physical_chemical_attributes);
        console.log(`  - formData.case.case_information exists:`, !!formData.case?.case_information);
        console.log(`  - formData.case.case_information.physical_chemical_attributes:`, formData.case?.case_information?.physical_chemical_attributes);
        console.log(`  - All case keys:`, formData.case ? Object.keys(formData.case) : 'No case data');

        // Clean up empty sections before saving
        const cleanedProduct = cleanupEmptySections(product);
        console.log(`Cleaned product ${i + 1}:`, cleanedProduct);

        // Special debugging for physical_chemical_attributes
        if (product.physical_chemical_attributes) {
          console.log(`🔍 PHYSICAL_CHEMICAL_ATTRIBUTES DEBUG:`);
          console.log(`  - Original:`, product.physical_chemical_attributes);
          console.log(`  - After cleanup:`, cleanedProduct.physical_chemical_attributes);
          console.log(`  - Has data:`, Object.keys(product.physical_chemical_attributes || {}).length > 0);
        }

        // Ensure physical_chemical_attributes is explicitly preserved in the cleaned product
        if (product.physical_chemical_attributes && !cleanedProduct.physical_chemical_attributes) {
          console.log(`🔧 RESTORING physical_chemical_attributes to cleaned product`);
          cleanedProduct.physical_chemical_attributes = product.physical_chemical_attributes;
        }

        // Ensure ingredients sections are explicitly preserved in the cleaned product
        if (product.ingredients && !cleanedProduct.ingredients) {
          console.log(`🔧 RESTORING ingredients to cleaned product`);
          cleanedProduct.ingredients = product.ingredients;
        }

        if (product.allergens && !cleanedProduct.allergens) {
          console.log(`🔧 RESTORING allergens to cleaned product`);
          cleanedProduct.allergens = product.allergens;
        }

        if (product.nutritional_information && !cleanedProduct.nutritional_information) {
          console.log(`🔧 RESTORING nutritional_information to cleaned product`);
          cleanedProduct.nutritional_information = product.nutritional_information;
        }

        // CRITICAL: Ensure physical_chemical_attributes is ALWAYS included in product data
        // Check if it exists in the original formData and add it to the cleaned product
        if (formData.products && formData.products[i] && formData.products[i].physical_chemical_attributes) {
          cleanedProduct.physical_chemical_attributes = formData.products[i].physical_chemical_attributes;
          console.log(`🔧 FORCE ADDED physical_chemical_attributes from formData:`, cleanedProduct.physical_chemical_attributes);
        }

        // CRITICAL: Ensure ingredients sections are ALWAYS included in product data
        if (formData.products && formData.products[i]) {
          if (formData.products[i].ingredients) {
            cleanedProduct.ingredients = formData.products[i].ingredients;
            console.log(`🔧 FORCE ADDED ingredients from formData:`, cleanedProduct.ingredients);
          }

          if (formData.products[i].allergens) {
            cleanedProduct.allergens = formData.products[i].allergens;
            console.log(`🔧 FORCE ADDED allergens from formData:`, cleanedProduct.allergens);
          }

          if (formData.products[i].nutritional_information) {
            cleanedProduct.nutritional_information = formData.products[i].nutritional_information;
            console.log(`🔧 FORCE ADDED nutritional_information from formData:`, cleanedProduct.nutritional_information);
          }
        }

        // Also check if it exists in the case data (sometimes it's stored there)
        if (formData.case && formData.case.physical_chemical_attributes && !cleanedProduct.physical_chemical_attributes) {
          cleanedProduct.physical_chemical_attributes = formData.case.physical_chemical_attributes;
          console.log(`🔧 COPIED physical_chemical_attributes from case data:`, cleanedProduct.physical_chemical_attributes);
        }

        // CRITICAL: Copy physical_chemical_attributes from case information to product
        // This section exists in case information and needs to be added to each product
        if (formData.case && formData.case.physical_chemical_attributes) {
          cleanedProduct.physical_chemical_attributes = { ...formData.case.physical_chemical_attributes };
          console.log(`✅ COPIED physical_chemical_attributes from case to product ${i + 1}:`, cleanedProduct.physical_chemical_attributes);
        } else if (formData.case && formData.case.case_information && formData.case.case_information.physical_chemical_attributes) {
          // Check if it's nested under case_information
          cleanedProduct.physical_chemical_attributes = { ...formData.case.case_information.physical_chemical_attributes };
          console.log(`✅ COPIED physical_chemical_attributes from case.case_information to product ${i + 1}:`, cleanedProduct.physical_chemical_attributes);
        } else {
          // Fallback: check other possible sources
          const physicalChemicalData =
            formData.products?.[i]?.physical_chemical_attributes ||
            product.physical_chemical_attributes ||
            {};

          if (Object.keys(physicalChemicalData).length > 0) {
            cleanedProduct.physical_chemical_attributes = physicalChemicalData;
            console.log(`� FALLBACK: Added physical_chemical_attributes to product ${i + 1}:`, physicalChemicalData);
          } else {
            console.log(`⚠️ WARNING: No physical_chemical_attributes data found for product ${i + 1}`);
          }
        }

        // CRITICAL: Copy microbiological_properties from case information to each product
        // This ensures each product has the same microbiological properties as the case
        if (formData.case && formData.case.microbiological_properties) {
          cleanedProduct.microbiological_properties = { ...formData.case.microbiological_properties };
          console.log(`✅ COPIED microbiological_properties from case to product ${i + 1}:`, cleanedProduct.microbiological_properties);
        } else if (formData.case && formData.case.case_information && formData.case.case_information.microbiological_properties) {
          cleanedProduct.microbiological_properties = { ...formData.case.case_information.microbiological_properties };
          console.log(`✅ COPIED microbiological_properties from case.case_information to product ${i + 1}:`, cleanedProduct.microbiological_properties);
        } else {
          console.log(`⚠️ WARNING: No case-level microbiological_properties found for product ${i + 1}`);
          // Ensure the section exists even if empty
          if (!cleanedProduct.microbiological_properties) {
            cleanedProduct.microbiological_properties = {
              "Total Plate Count": "",
              "Yeast and Mold": ""
            };
          }
        }

        allCleanedProducts.push(cleanedProduct);
      }

      // Save single combined document with case information and all products
      if (formData.case) {
        // Create timestamp in local timezone format
        const now = new Date();
        // Get timezone offset and adjust for local time
        const timezoneOffset = now.getTimezoneOffset() * 60000; // offset in milliseconds
        const localTime = new Date(now.getTime() - timezoneOffset);
        const localTimestamp = localTime.toISOString().slice(0, -1); // Remove 'Z' to indicate local time

        console.log('=== TIMESTAMP DEBUG ===');
        console.log('Current local time:', now.toString());
        console.log('UTC timestamp (old way):', now.toISOString());
        console.log('Local timestamp (new way):', localTimestamp);
        console.log('Local time hours:', now.getHours());
        console.log('Local time minutes:', now.getMinutes());
        console.log('Timezone offset (minutes):', now.getTimezoneOffset());
        console.log('=======================');

        const combinedData = {
          metadata: {
            email: email,
            bucket_name: bucketName,
            object_name: objectName,
            timestamp: localTimestamp, // This will be overridden by backend
            local_timestamp: localTimestamp, // This should be preserved by backend
            job_id: jobId, // Single job ID for the combined document
            record_type: "case_with_products", // Identify this as a combined record
            content_type: "case_and_products", // Indicates this document contains both case and product information
            product_count: allCleanedProducts.length, // Track number of products
            customer: customer || '',
          },
          job_details: {
            job_id: jobId,
            bucket_name: bucketName,
            object_name: objectName,
            email: email,
          },
          case: formData.case, // Save case information
          products: allCleanedProducts, // Include all cleaned products
          // Alternative structure to prevent backend splitting
          case_information: formData.case,
          product_list: allCleanedProducts,
          combined_document: true,
        };

        // VERIFY: This should be the NEW combined document structure
        console.log("🔍 VERIFYING NEW STRUCTURE:");
        console.log("  - Record type:", combinedData.metadata.record_type);
        console.log("  - Content type:", combinedData.metadata.content_type);
        console.log("  - Has case data:", !!combinedData.case);
        console.log("  - Products count:", combinedData.products.length);
        console.log("  - Has config section:", !!combinedData.config);
        console.log("  - Has custom_fields section:", !!combinedData.custom_fields);

        console.log("🎯 SINGLE SAVE CALL - Saving Combined Case and Products Document:", combinedData);
        console.log(`📦 Document contains ${allCleanedProducts.length} products`);
        console.log(`🔑 Job ID: ${jobId} - This should be the ONLY document created`);

        try {
          const requestPayload = {
            ...combinedData,
            upsert: true,
            // Add flag to tell backend NOT to split into individual products
            save_as_single_document: true,
            prevent_product_splitting: true
          };

          console.log("📤 SENDING TO BACKEND:", requestPayload);

          const response = await axios.post(baseURLSavePDFToMongo,
            requestPayload,
            {
              headers: {
                "Content-Type": "application/json",
              },
            }
          );
          savedCase = response.data;
          console.log("Combined document saved successfully:", response.data);
        } catch (error) {
          console.error("Error saving combined document:", error);
          console.error("Error response:", error.response?.data);
          console.error("Error status:", error.response?.status);
          console.error("Error headers:", error.response?.headers);

          // Handle duplicate detection (409 Conflict)
          if (error.response?.status === 409 && error.response?.data?.status === 'duplicate_found') {
            const duplicateInfo = error.response.data.duplicate_info;
            const newData = error.response.data.new_data;

            // Show duplicate confirmation modal
            showDuplicateModal(duplicateInfo, newData);
            return; // Don't show error toast for duplicates
          }

          toast.error(`Failed to save combined document: ${error.response?.data?.message || error.message}`, {
            position: "bottom-right",
            autoClose: 5000,
          });
          throw error;
        }
      }

      // Clear the changed fields after successful save
      setChangedFields(new Set());

      // Create appropriate success message
      let successMessage = "";
      if (formData.case && allCleanedProducts.length > 0) {
        successMessage = `Successfully saved combined document with case information and ${allCleanedProducts.length} product(s)`;
      } else if (formData.case) {
        successMessage = "Successfully saved case information";
      } else {
        successMessage = "Save completed (no data to save)";
      }

      toast.success(successMessage, {
        position: "bottom-right",
        autoClose: 3000,
      });

      return {
        case: formData.case,
        products: allCleanedProducts,
        combinedDocument: savedCase
      };
    } catch (error) {
      console.error("Error in saveToDatabase:", error);
      throw error;
    } finally {
      // Reset saving state
      setIsSaving(false);
    }
  };

  // Function to handle ingredients image selection
  const handleIngredientsImageSelection = (imageIndex) => {
    setSelectedIngredientsImage(imageIndex);
    console.log(`🎯 Selected ingredients image: ${imageIndex}`);

    // Find the selected image data
    const selectedImage = ingredientsData.find(img => img.image_index === imageIndex);
    if (selectedImage) {
      console.log("📋 Selected ingredients data:", selectedImage.analysis);
      toast.info(`Selected Image ${imageIndex} for ingredients data`, {
        position: "bottom-right",
        autoClose: 2000,
      });
    }
  };

  // Function to parse ingredients text into clean array
  const parseIngredientsToArray = (ingredientsText) => {
    if (!ingredientsText) return [];

    // Remove the header text like "Ingredients List:**" or "Complete Ingredients List:**"
    let cleanText = ingredientsText.replace(/^.*?Ingredients List\*?\*?:?\s*/i, '');

    // Split by newlines and clean up each item
    const items = cleanText.split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => {
        // Remove leading dashes, bullets, or numbers
        return item.replace(/^[-•*\d+\.\)\s]+/, '').trim();
      })
      .filter(item => item.length > 0);

    return items;
  };

  // Function to parse allergens text into clean array
  const parseAllergensToArray = (allergensText) => {
    if (!allergensText) return [];

    // Remove the header text like "Allergen Information:**"
    let cleanText = allergensText.replace(/^.*?Allergen Information\*?\*?:?\s*/i, '');

    // Split by newlines and clean up each item
    const items = cleanText.split('\n')
      .map(item => item.trim())
      .filter(item => item.length > 0)
      .map(item => {
        // Remove leading dashes, bullets, or numbers
        return item.replace(/^[-•*\d+\.\)\s]+/, '').trim();
      })
      .filter(item => item.length > 0);

    return items;
  };

  // Function to get the currently selected ingredients data
  const getSelectedIngredientsData = () => {
    if (!selectedIngredientsImage) return null;

    const selectedImage = ingredientsData.find(img => img.image_index === selectedIngredientsImage);
    if (!selectedImage || !selectedImage.ingredients_found) return null;

    // Parse the analysis to extract structured data
    const analysis = selectedImage.analysis;
    const ingredientsMatch = analysis.match(/(?:Complete Ingredients List|Ingredients List)[\s\S]*?(?=\n\d+\.|$)/i);
    const allergenMatch = analysis.match(/(?:Allergen Information)[\s\S]*?(?=\n\d+\.|$)/i);
    const nutritionMatch = analysis.match(/(?:Nutritional Information)[\s\S]*?(?=\n\d+\.|$)/i);

    // Parse ingredients and allergens into array format
    const ingredientsArray = ingredientsMatch ? parseIngredientsToArray(ingredientsMatch[0]) : [];
    const allergensArray = allergenMatch ? parseAllergensToArray(allergenMatch[0]) : [];

    return {
      imageIndex: selectedImage.image_index,
      imageUrl: selectedImage.image_url,
      fullAnalysis: analysis,
      ingredients: ingredientsArray,
      allergens: allergensArray,
      nutrition: nutritionMatch ? nutritionMatch[0].trim() : null
    };
  };

  // Function to handle hi-res image upload
  const handleHiResImageUpload = async () => {
    console.log("🖼️ IMPORT HI-RES IMAGES FUNCTION CALLED");

    // Create file input element
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.multiple = true;
    fileInput.accept = 'image/*';

    fileInput.onchange = async (event) => {
      const files = Array.from(event.target.files);

      if (files.length === 0) {
        console.log("❌ No files selected");
        return;
      }

      console.log(`📁 Selected ${files.length} image(s):`, files.map(f => f.name));

      // Set loading state and change cursor to wait
      setIsUploadingImages(true);
      setUploadProgress({ current: 0, total: files.length });
      document.body.style.cursor = 'wait';

      try {
        // Use the same S3 path structure as the PDF upload
        const baseURLAddPhotoToS3 = process.env.REACT_APP_ENV === "PROD"
          ? GLOBALS.PROD.baseURLAddPhotoToS3
          : GLOBALS.DEV.baseURLAddPhotoToS3;

        let completedUploads = 0;

        const uploadPromises = files.map(async (file, index) => {
          const formData = new FormData();
          formData.append("file", file);
          formData.append("email", email);
          // Upload to the same folder as the PDF (email/tmp/)
          formData.append("folder_path", `${email}/tmp`);

          console.log(`⬆️ Uploading ${file.name} to S3...`);

          const response = await axios.post(baseURLAddPhotoToS3, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          console.log(`✅ Successfully uploaded ${file.name}:`, response.data);

          // Update progress
          completedUploads++;
          setUploadProgress({ current: completedUploads, total: files.length });

          return {
            fileName: file.name,
            url: response.data.url,
            success: true
          };
        });

        const results = await Promise.allSettled(uploadPromises);

        const successful = results.filter(r => r.status === 'fulfilled').map(r => r.value);
        const failed = results.filter(r => r.status === 'rejected');

        console.log(`📊 Upload Results: ${successful.length} successful, ${failed.length} failed`);

        if (successful.length > 0) {
          console.log("✅ Successfully uploaded images:", successful);
          toast.success(`Successfully uploaded ${successful.length} hi-res image(s) to S3`, {
            position: "bottom-right",
            autoClose: 3000,
          });

          // Process uploaded images through OpenAI for ingredients
          console.log("🧠 Processing images through OpenAI for ingredients...");
          setIsProcessingWithOpenAI(true);
          await processImagesForIngredients(successful.map(img => img.url));
          setIsProcessingWithOpenAI(false);
        }

        if (failed.length > 0) {
          console.error("❌ Failed uploads:", failed);
          toast.error(`Failed to upload ${failed.length} image(s)`, {
            position: "bottom-right",
            autoClose: 5000,
          });
        }

      } catch (error) {
        console.error("❌ Error uploading hi-res images:", error);
        toast.error("Failed to upload hi-res images", {
          position: "bottom-right",
          autoClose: 5000,
        });
      } finally {
        // Reset loading state and cursor
        setIsUploadingImages(false);
        setUploadProgress({ current: 0, total: 0 });
        setIsProcessingWithOpenAI(false);
        document.body.style.cursor = 'default';
      }
    };

    // Trigger file selection dialog
    fileInput.click();
  };

  // Function to process images through OpenAI for ingredients
  const processImagesForIngredients = async (imageUrls) => {
    try {
      console.log("🔍 Sending images to OpenAI for ingredients analysis...");

      const baseURLProcessImagesForIngredients = process.env.REACT_APP_ENV === "PROD"
        ? GLOBALS.PROD.baseURLProcessImagesForIngredients
        : GLOBALS.DEV.baseURLProcessImagesForIngredients;

      const response = await axios.post(baseURLProcessImagesForIngredients, {
        image_urls: imageUrls
      }, {
        headers: {
          "Content-Type": "application/json",
        },
      });

      console.log("🧠 OpenAI Analysis Results:", response.data);

      // Log a prominent header for ingredients analysis
      console.log("\n" + "🎯".repeat(20));
      console.log("🎯 INGREDIENTS ANALYSIS COMPLETE 🎯");
      console.log("🎯".repeat(20));

      // Check if any ingredients were found
      const ingredientsResults = response.data.results.filter(result => result.ingredients_found);

      // Store ingredients data for the tab display
      setIngredientsData(response.data.results);

      // Automatically select the first image with ingredients
      if (ingredientsResults.length > 0) {
        setSelectedIngredientsImage(ingredientsResults[0].image_index);
      }

      if (ingredientsResults.length > 0) {
        console.log("🎯 INGREDIENTS FOUND in uploaded images!");
        console.log("=".repeat(80));

        // Log detailed results for each image with ingredients
        ingredientsResults.forEach((result, index) => {
          console.log(`📋 IMAGE ${result.image_index} - INGREDIENTS DETAILS:`);
          console.log(`🔗 Image URL: ${result.image_url}`);
          console.log("📄 FULL ANALYSIS:");
          console.log(result.analysis);
          console.log("=".repeat(80));
        });

        // Also log a summary table for easy reading
        console.log("📊 INGREDIENTS SUMMARY:");
        ingredientsResults.forEach((result, index) => {
          console.log(`\n🖼️ Image ${result.image_index}:`);

          // Try to extract structured data from the analysis
          const analysis = result.analysis;

          // Extract ingredients list
          const ingredientsMatch = analysis.match(/(?:Complete Ingredients List|Ingredients List)[\s\S]*?(?=\n\d+\.|$)/i);
          if (ingredientsMatch) {
            console.log("🧪 INGREDIENTS:");
            console.log(ingredientsMatch[0]);
          }

          // Extract allergen info
          const allergenMatch = analysis.match(/(?:Allergen Information)[\s\S]*?(?=\n\d+\.|$)/i);
          if (allergenMatch) {
            console.log("⚠️ ALLERGENS:");
            console.log(allergenMatch[0]);
          }

          // Extract nutritional info
          const nutritionMatch = analysis.match(/(?:Nutritional Information)[\s\S]*?(?=\n\d+\.|$)/i);
          if (nutritionMatch) {
            console.log("📊 NUTRITION:");
            console.log(nutritionMatch[0]);
          }
        });

        toast.success(`Found ingredients information in ${ingredientsResults.length} image(s)! Check the Ingredients tab and browser console for details.`, {
          position: "bottom-right",
          autoClose: 5000,
        });

        // Automatically switch to Ingredients tab when ingredients are found
        setTimeout(() => {
          setActiveTab("ingredients");
        }, 1000);
      } else {
        console.log("ℹ️ No ingredients information found in uploaded images");
        toast.info("No ingredients information detected in uploaded images", {
          position: "bottom-right",
          autoClose: 3000,
        });
      }

      // Log summary
      console.log("\n📊 FINAL PROCESSING SUMMARY:");
      console.log("=".repeat(50));
      console.log(`📈 Total images processed: ${response.data.summary.total_images}`);
      console.log(`✅ Successfully analyzed: ${response.data.summary.successfully_processed}`);
      console.log(`🎯 Images with ingredients: ${response.data.summary.ingredients_found_count}`);
      console.log("=".repeat(50));

      if (response.data.summary.ingredients_found_count > 0) {
        console.log("🎉 SUCCESS! Ingredients information extracted and logged above.");
        console.log("📋 Scroll up to see detailed ingredients, allergens, and nutrition info.");
      } else {
        console.log("ℹ️ No ingredients found in the uploaded images.");
      }
      console.log("=".repeat(50));

    } catch (error) {
      console.error("❌ Error processing images for ingredients:", error);
      toast.error("Failed to analyze images for ingredients", {
        position: "bottom-right",
        autoClose: 5000,
      });
    } finally {
      setIsProcessingWithOpenAI(false);
    }
  };

  const addRequiredMicrobiologicalFields = (productIndex) => {
    setFormData(prevData => ({
      ...prevData,
      products: prevData.products.map((product, idx) => {
        if (idx === productIndex) {
          const updatedProduct = { ...product };

          // Ensure microbiological_properties section exists
          if (!updatedProduct.microbiological_properties) {
            updatedProduct.microbiological_properties = {};
          }

          // Add required fields if they don't exist
          if (!updatedProduct.microbiological_properties['Total Plate Count']) {
            updatedProduct.microbiological_properties['Total Plate Count'] = '';
          }
          if (!updatedProduct.microbiological_properties['Yeast and Mold']) {
            updatedProduct.microbiological_properties['Yeast and Mold'] = '';
          }

          return updatedProduct;
        }
        return product;
      })
    }));

    // Mark fields as changed
    setChangedFields(prev => new Set([
      ...prev,
      `${productIndex}-microbiological_properties-Total Plate Count`,
      `${productIndex}-microbiological_properties-Yeast and Mold`
    ]));
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          alignItems: "center",
          height: "100vh",
          paddingTop: "20px",
        }}
      >
        <Bars
          height="80"
          width="80"
          color="#2196f3"
          ariaLabel="bars-loading"
          wrapperStyle={{}}
          wrapperClass=""
          visible={true}
        />
        <div style={{ marginTop: "10px" }}>Extracting data ...</div>
      </div>
    );
  }

  if (error) {
    return <div>Error: {error.message}</div>;
  }

  const numberOfProducts =
    parseInt(formData.how_many_products_are_on_this_page) || 1;
  const productTabs = Array.from({ length: numberOfProducts }, (_, i) => i + 1);

  const formFields = Object.entries(formData).filter(
    ([key]) => key !== "how_many_products_are_on_this_page"
  );







  return (
    <Container fluid style={{ backgroundColor: "#f5f5f5", minHeight: "100vh", padding: "20px" }}>
      <div className="d-flex align-items-center mb-3">
        <Button
          variant="outline-success"
          onClick={saveToDatabase}
          disabled={isSaving}
          style={{
            cursor: isSaving ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {isSaving && (
            <RotatingLines
              strokeColor="#198754"
              strokeWidth="3"
              animationDuration="0.75"
              width="16"
              visible={true}
            />
          )}
          {isSaving
            ? "Saving..."
            : changedFields.size > 0
            ? `Save Changes (${changedFields.size})`
            : "Save"}
          {selectedIngredientsImage && !isSaving && (
            <span className="badge bg-success text-white ms-2">
              +Ingredients
            </span>
          )}
        </Button>
        <Button
          variant="outline-primary"
          onClick={handleHiResImageUpload}
          disabled={isUploadingImages || isProcessingWithOpenAI}
          className="ms-2"
          style={{
            cursor: (isUploadingImages || isProcessingWithOpenAI) ? 'wait' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}
        >
          {(isUploadingImages || isProcessingWithOpenAI) && (
            <RotatingLines
              strokeColor="#0d6efd"
              strokeWidth="3"
              animationDuration="0.75"
              width="16"
              visible={true}
            />
          )}
          {isUploadingImages
            ? `Uploading Images... (${uploadProgress.current}/${uploadProgress.total})`
            : isProcessingWithOpenAI
            ? "Analyzing with OpenAI..."
            : "Import Hi-Res Images"}
        </Button>
        {changedFields.size > 0 && (
          <span className="ms-3 text-danger">
            Unsaved Changes
          </span>
        )}
      </div>

      {/* Upload Progress Indicator */}
      {(isUploadingImages || isProcessingWithOpenAI) && (
        <div className="mb-3" style={{
          padding: '12px',
          backgroundColor: isProcessingWithOpenAI ? '#f3e5f5' : '#e3f2fd',
          borderRadius: '6px',
          border: isProcessingWithOpenAI ? '1px solid #9c27b0' : '1px solid #2196f3',
          display: 'flex',
          alignItems: 'center',
          gap: '12px'
        }}>
          <RotatingLines
            strokeColor={isProcessingWithOpenAI ? "#9c27b0" : "#2196f3"}
            strokeWidth="3"
            animationDuration="0.75"
            width="20"
            visible={true}
          />
          <div>
            <div style={{ fontWeight: 'bold', color: isProcessingWithOpenAI ? '#7b1fa2' : '#1976d2' }}>
              {isUploadingImages ? "Uploading Hi-Res Images..." : "Analyzing Images with OpenAI..."}
            </div>
            <div style={{ fontSize: '14px', color: '#666' }}>
              {isUploadingImages
                ? `Progress: ${uploadProgress.current} of ${uploadProgress.total} files completed`
                : "Looking for ingredients and nutritional information..."
              }
            </div>
          </div>
        </div>
      )}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >








              {/* Case Information Tab - Displays case-level information only */}
              <Tab eventKey="case" title="Case Information">
                <div className="p-3 border rounded">
                  <h5 className="mb-3">Case Information</h5>
                  <p className="text-muted mb-4">
                    This section contains case-level information that applies to the entire document/shipment.
                    Product-specific information is displayed in the individual Product tabs below.
                  </p>

                  {/* Display case-level fields and custom fields only */}
                  {(() => {
                    const caseSections = {};

                    // Add case sections only
                    if (formData.case) {
                      Object.entries(formData.case).forEach(([sectionKey, sectionData]) => {
                        caseSections[sectionKey] = sectionData;
                      });
                    }



                    return Object.entries(caseSections).map(([sectionKey, sectionData]) => (
                      <div key={sectionKey} className="mb-4">
                        <h6 className="text-primary mb-3">
                          {sectionKey.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </h6>
                        <div className="row">
                          {Object.entries(sectionData || {}).map(([fieldKey, fieldValue]) => (
                            <div key={fieldKey} className="col-md-6 mb-3">
                              <Form.Group>
                                <Form.Label>
                                  {fieldKey}
                                  {fieldKey === 'Product Type' && (
                                    <span className="text-muted ms-2">(Auto-determined)</span>
                                  )}
                                  {fieldKey === 'Case UPC' && renderValidationBadge(validationStates.case_upc)}
                                </Form.Label>
                                <Form.Control
                                  type="text"
                                  value={fieldValue || ''}
                                  disabled={fieldKey === 'Product Type'}
                                  readOnly={fieldKey === 'Product Type'}
                                  onChange={async (e) => {
                                    if (fieldKey === 'Product Type') return; // Prevent changes to Product Type

                                    const rawValue = e.target.value;
                                    // Clean UPC values by removing spaces for Case UPC field
                                    const newValue = fieldKey === 'Case UPC' ? cleanUPCValue(rawValue) : rawValue;

                                    // Update case field
                                    setFormData(prevData => ({
                                      ...prevData,
                                      case: {
                                        ...prevData.case,
                                        [sectionKey]: {
                                          ...prevData.case[sectionKey],
                                          [fieldKey]: newValue
                                        }
                                      }
                                    }));
                                    setChangedFields(prev => new Set([...prev, `case.${sectionKey}.${fieldKey}`]));

                                    // Real-time GTIN validation for Case UPC
                                    if (fieldKey === 'Case UPC' && newValue && newValue.trim()) {
                                      console.log('=== REAL-TIME CASE UPC VALIDATION ===');
                                      console.log('Validating Case UPC:', newValue);

                                      try {
                                        const validationResult = await validateGTIN(newValue, 'case');
                                        setValidationStates(prev => ({
                                          ...prev,
                                          case_upc: validationResult
                                        }));

                                        // Update case validation flag in form data
                                        setFormData(prevData => ({
                                          ...prevData,
                                          case: {
                                            ...prevData.case,
                                            validation: {
                                              ...prevData.case?.validation,
                                              gtin_valid: validationResult?.status === 'valid'
                                            }
                                          }
                                        }));

                                        // Show toast notification
                                        if (validationResult.status === 'valid') {
                                          toast.success(`Case UPC validated successfully: ${newValue}`, {
                                            position: "top-right",
                                            autoClose: 3000,
                                          });
                                        } else if (validationResult.status === 'invalid') {
                                          toast.warning(`Invalid Case UPC: ${newValue} - ${validationResult.message}`, {
                                            position: "top-right",
                                            autoClose: 3000,
                                          });
                                        } else if (validationResult.status === 'error') {
                                          toast.error(`Could not validate Case UPC: ${newValue}`, {
                                            position: "top-right",
                                            autoClose: 3000,
                                          });
                                        }
                                      } catch (error) {
                                        console.error('Real-time validation error:', error);
                                        setValidationStates(prev => ({
                                          ...prev,
                                          case_upc: {
                                            status: 'error',
                                            message: `Validation error: ${error.message}`,
                                            timestamp: new Date().toISOString()
                                          }
                                        }));

                                        // Set validation flag to false on error
                                        setFormData(prevData => ({
                                          ...prevData,
                                          case: {
                                            ...prevData.case,
                                            validation: {
                                              ...prevData.case?.validation,
                                              gtin_valid: false
                                            }
                                          }
                                        }));
                                      }
                                    }
                                  }}
                                  style={{
                                    backgroundColor: fieldKey === 'Product Type' ? '#f8f9fa' : 'white',
                                    cursor: fieldKey === 'Product Type' ? 'not-allowed' : 'text'
                                  }}
                                />
                              </Form.Group>
                            </div>
                          ))}
                        </div>
                      </div>
                    ));
                  })()}




                </div>
              </Tab>

              {/* Ingredients Tab */}
              <Tab
                eventKey="ingredients"
                title={
                  <span style={{
                    color: ingredientsData.filter(r => r.ingredients_found).length > 0 ? '#28a745' : '#6c757d',
                    fontWeight: ingredientsData.filter(r => r.ingredients_found).length > 0 ? 'bold' : 'normal'
                  }}>
                    🧪 Ingredients {ingredientsData.filter(r => r.ingredients_found).length > 0 && `(${ingredientsData.filter(r => r.ingredients_found).length})`}
                  </span>
                }
              >
                <div className="p-3 border rounded">
                  <h5 className="mb-3">
                    🧪 Ingredients Information from Hi-Res Images
                  </h5>

                  {(() => {
                    // Filter to only show images with ingredients found
                    const imagesWithIngredients = ingredientsData.filter(result => result.ingredients_found);

                    if (imagesWithIngredients.length === 0) {
                      return (
                        <div className="text-center py-5">
                          <div className="mb-3">
                            <i className="fas fa-image" style={{ fontSize: '3rem', color: '#dee2e6' }}></i>
                          </div>
                          <h6 className="text-muted">No ingredients found in uploaded images</h6>
                          <p className="text-muted">
                            {ingredientsData.length > 0
                              ? `Processed ${ingredientsData.length} image(s), but no ingredients information was detected. Try uploading images that clearly show ingredient labels.`
                              : "Upload hi-res images using the \"Import Hi-Res Images\" button above to extract ingredients information."
                            }
                          </p>
                        </div>
                      );
                    }

                    return (
                      <div>
                        <div className="mb-4">
                          <div className="alert alert-success">
                            <strong>🎯 Ingredients Found!</strong>
                            {` Showing ${imagesWithIngredients.length} image(s) with ingredients information`}
                            {ingredientsData.length > imagesWithIngredients.length &&
                              ` (${ingredientsData.length - imagesWithIngredients.length} image(s) without ingredients were filtered out)`
                            }
                          </div>
                          {imagesWithIngredients.length > 1 && (
                            <div className="alert alert-info">
                              <strong>📋 Select Ingredients Source:</strong> Choose which image's ingredients data to use for your product information.
                            </div>
                          )}

                          {selectedIngredientsImage && (
                            <div className="mb-3">
                              <button
                                className="btn btn-outline-primary btn-sm"
                                onClick={() => {
                                  const selectedData = getSelectedIngredientsData();
                                  console.log("🎯 Currently selected ingredients data:", selectedData);
                                  toast.success("Selected ingredients data logged to console", {
                                    position: "bottom-right",
                                    autoClose: 3000,
                                  });
                                }}
                              >
                                📋 Use Selected Ingredients Data
                              </button>
                              <small className="text-muted ms-2">
                                Click to see how the selected ingredients data can be used
                              </small>
                            </div>
                          )}
                        </div>

                        {imagesWithIngredients.map((result, index) => (
                        <div key={index} className="mb-4">
                          <div className={`card ${selectedIngredientsImage === result.image_index ? 'border-primary' : ''}`}>
                            <div className="card-header d-flex justify-content-between align-items-center">
                              <div className="d-flex align-items-center">
                                <div className="form-check me-3">
                                  <input
                                    className="form-check-input"
                                    type="radio"
                                    name="ingredientsImageSelection"
                                    id={`ingredients-radio-${result.image_index}`}
                                    checked={selectedIngredientsImage === result.image_index}
                                    onChange={() => handleIngredientsImageSelection(result.image_index)}
                                  />
                                  <label className="form-check-label" htmlFor={`ingredients-radio-${result.image_index}`}>
                                    <span className="visually-hidden">Select this image for ingredients data</span>
                                  </label>
                                </div>
                                <h6 className="mb-0">
                                  🖼️ Image {result.image_index}
                                  <span className="badge bg-success ms-2">Ingredients Found</span>
                                  {selectedIngredientsImage === result.image_index && (
                                    <span className="badge bg-primary ms-2">Selected</span>
                                  )}
                                </h6>
                              </div>
                              <small className="text-muted">
                                <a href={result.image_url} target="_blank" rel="noopener noreferrer">
                                  View Image
                                </a>
                              </small>
                            </div>
                            <div className="card-body">
                              {result.processed_successfully ? (
                                <div>
                                  {/* Parse and display structured ingredients data */}
                                  {(() => {
                                    const analysis = result.analysis;

                                    // Extract ingredients list
                                    const ingredientsMatch = analysis.match(/(?:Complete Ingredients List|Ingredients List)[\s\S]*?(?=\n\d+\.|$)/i);
                                    const allergenMatch = analysis.match(/(?:Allergen Information)[\s\S]*?(?=\n\d+\.|$)/i);
                                    const nutritionMatch = analysis.match(/(?:Nutritional Information)[\s\S]*?(?=\n\d+\.|$)/i);

                                    return (
                                      <div>
                                        {ingredientsMatch && (
                                          <div className="mb-3">
                                            <h6 className="text-primary">🧪 Ingredients:</h6>
                                            <div className="bg-light p-3 rounded">
                                              {(() => {
                                                const ingredientsArray = parseIngredientsToArray(ingredientsMatch[0]);
                                                return (
                                                  <div>
                                                    <div className="mb-2">
                                                      <small className="text-muted">
                                                        Parsed into {ingredientsArray.length} ingredient(s):
                                                      </small>
                                                    </div>
                                                    <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                                                      {ingredientsArray.map((ingredient, idx) => (
                                                        <li key={idx} className="mb-1">{ingredient}</li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                            <details className="mt-2">
                                              <summary className="btn btn-outline-secondary btn-sm">
                                                View Raw Text
                                              </summary>
                                              <div className="mt-2 p-2 bg-light rounded">
                                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', margin: 0 }}>
                                                  {ingredientsMatch[0].trim()}
                                                </pre>
                                              </div>
                                            </details>
                                          </div>
                                        )}

                                        {allergenMatch && (
                                          <div className="mb-3">
                                            <h6 className="text-warning">⚠️ Allergens:</h6>
                                            <div className="bg-light p-3 rounded">
                                              {(() => {
                                                const allergensArray = parseAllergensToArray(allergenMatch[0]);
                                                return (
                                                  <div>
                                                    <div className="mb-2">
                                                      <small className="text-muted">
                                                        Parsed into {allergensArray.length} allergen statement(s):
                                                      </small>
                                                    </div>
                                                    <ul className="mb-0" style={{ fontSize: '0.9rem' }}>
                                                      {allergensArray.map((allergen, idx) => (
                                                        <li key={idx} className="mb-1">{allergen}</li>
                                                      ))}
                                                    </ul>
                                                  </div>
                                                );
                                              })()}
                                            </div>
                                            <details className="mt-2">
                                              <summary className="btn btn-outline-secondary btn-sm">
                                                View Raw Text
                                              </summary>
                                              <div className="mt-2 p-2 bg-light rounded">
                                                <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.8rem', margin: 0 }}>
                                                  {allergenMatch[0].trim()}
                                                </pre>
                                              </div>
                                            </details>
                                          </div>
                                        )}

                                        {nutritionMatch && (
                                          <div className="mb-3">
                                            <h6 className="text-info">📊 Nutrition:</h6>
                                            <div className="bg-light p-3 rounded">
                                              <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.9rem', margin: 0 }}>
                                                {nutritionMatch[0].trim()}
                                              </pre>
                                            </div>
                                          </div>
                                        )}

                                        {/* Full analysis in collapsible section */}
                                        <details className="mt-3">
                                          <summary className="btn btn-outline-secondary btn-sm">
                                            View Full Analysis
                                          </summary>
                                          <div className="mt-2 p-3 bg-light rounded">
                                            <pre style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem', margin: 0 }}>
                                              {analysis}
                                            </pre>
                                          </div>
                                        </details>
                                      </div>
                                    );
                                  })()}
                                </div>
                              ) : (
                                <div className="text-danger">
                                  <p>❌ Error processing this image:</p>
                                  <code>{result.error}</code>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                      </div>
                    );
                  })()}
                </div>
              </Tab>

              {/* Product Tabs */}
              {formData.products?.map((product, productIndex) => (
                <Tab
                  eventKey={productIndex}
                  title={
                    <span style={getProductTabStyle(productIndex)}>
                      {`Product ${productIndex + 1} - ${product.name}`}
                    </span>
                  }
                  key={productIndex}
                >
                  <div className="p-3 border rounded">
                    <h5 className="mb-3">
                      Product {productIndex + 1} - {product.name}
                    </h5>
                    {/* Product Information section only */}
                    {(() => {
                      const productInfoData = product.product_information || {};
                      const hasData = Object.keys(productInfoData).length > 0;

                      if (!hasData) {
                        return (
                          <div className="alert alert-info">
                            <h6>No Product Information Available</h6>
                            <p className="mb-0">
                              No product information was extracted for this product.
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="mb-4">
                          <h6 className="text-primary mb-3">Product Information</h6>
                          <div className="row">

                            {Object.entries(productInfoData).map(
                              ([fieldKey, fieldValue]) => {
                                const fieldId = `${productIndex}-product_information-${fieldKey}`;
                                const isChanged = changedFields.has(fieldId);



                                return (
                                  <div key={fieldKey} className="col-md-6 mb-3">
                                    <Form.Group>
                                      <Form.Label>
                                        {fieldKey}
                                        {fieldKey === 'Product Type' && (
                                          <span className="text-muted ms-2">(Fixed value for products)</span>
                                        )}
                                        {fieldKey === 'Retail Pkg. UPC' && renderValidationBadge(validationStates[`product_${productIndex}_retail_pkg_upc`])}
                                      </Form.Label>
                                      <Form.Control
                                        type="text"
                                        value={fieldValue || ""}
                                        disabled={fieldKey === 'Product Type'}
                                        readOnly={fieldKey === 'Product Type'}
                                        onChange={async (e) => {
                                          if (fieldKey === 'Product Type') return; // Prevent changes to Product Type

                                          const rawValue = e.target.value;
                                          // Clean UPC values by removing spaces for Retail Pkg. UPC field
                                          const newValue = fieldKey === 'Retail Pkg. UPC' ? cleanUPCValue(rawValue) : rawValue;

                                          setFormData(prevData => ({
                                            ...prevData,
                                            products: prevData.products.map((p, idx) =>
                                              idx === productIndex
                                                ? {
                                                    ...p,
                                                    product_information: {
                                                      ...p.product_information,
                                                      [fieldKey]: newValue
                                                    }
                                                  }
                                                : p
                                            )
                                          }));
                                          setChangedFields(prev => new Set([...prev, fieldId]));

                                          // Real-time GTIN validation for Retail Pkg. UPC
                                          if (fieldKey === 'Retail Pkg. UPC' && newValue && newValue.trim()) {
                                            console.log(`=== REAL-TIME RETAIL PKG. UPC VALIDATION (Product ${productIndex + 1}) ===`);
                                            console.log('Validating Retail Pkg. UPC:', newValue);

                                            try {
                                              const validationResult = await validateGTIN(newValue, 'product');
                                              setValidationStates(prev => ({
                                                ...prev,
                                                [`product_${productIndex}_retail_pkg_upc`]: validationResult
                                              }));

                                              // Update product validation flag in form data
                                              setFormData(prevData => ({
                                                ...prevData,
                                                products: prevData.products.map((p, idx) =>
                                                  idx === productIndex
                                                    ? {
                                                        ...p,
                                                        validation: {
                                                          ...p.validation,
                                                          gtin_valid: validationResult?.status === 'valid'
                                                        }
                                                      }
                                                    : p
                                                )
                                              }));

                                              // Show toast notification
                                              if (validationResult.status === 'valid') {
                                                toast.success(`Retail Pkg. UPC validated successfully for Product ${productIndex + 1}: ${newValue}`, {
                                                  position: "top-right",
                                                  autoClose: 3000,
                                                });
                                              } else if (validationResult.status === 'invalid') {
                                                toast.warning(`Invalid Retail Pkg. UPC for Product ${productIndex + 1}: ${newValue} - ${validationResult.message}`, {
                                                  position: "top-right",
                                                  autoClose: 3000,
                                                });
                                              } else if (validationResult.status === 'error') {
                                                toast.error(`Could not validate Retail Pkg. UPC for Product ${productIndex + 1}: ${newValue}`, {
                                                  position: "top-right",
                                                  autoClose: 3000,
                                                });
                                              }
                                            } catch (error) {
                                              console.error('Real-time validation error:', error);
                                              setValidationStates(prev => ({
                                                ...prev,
                                                [`product_${productIndex}_retail_pkg_upc`]: {
                                                  status: 'error',
                                                  message: `Validation error: ${error.message}`,
                                                  timestamp: new Date().toISOString()
                                                }
                                              }));

                                              // Set validation flag to false on error
                                              setFormData(prevData => ({
                                                ...prevData,
                                                products: prevData.products.map((p, idx) =>
                                                  idx === productIndex
                                                    ? {
                                                        ...p,
                                                        validation: {
                                                          ...p.validation,
                                                          gtin_valid: false
                                                        }
                                                      }
                                                    : p
                                                )
                                              }));
                                            }
                                          }
                                        }}
                                        style={{
                                          backgroundColor: fieldKey === 'Product Type' ? '#f8f9fa' : (isChanged ? "#fff3cd" : "white"),
                                          borderColor: isChanged ? "#ffc107" : "#ced4da",
                                          cursor: fieldKey === 'Product Type' ? 'not-allowed' : 'text'
                                        }}
                                      />
                                    </Form.Group>
                                  </div>
                                );
                              }
                            )}
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                </Tab>
              ))}
      </Tabs>







      {/* Duplicate Detection Modal */}
      <Modal show={duplicateModalVisible} onHide={() => {
        setDuplicateModalVisible(false);
        setDuplicateInfo(null);
        setNewDataForReplacement(null);
      }} size="lg" centered>
        <Modal.Header closeButton style={{ backgroundColor: '#fff7e6', borderBottom: '1px solid #ffd591' }}>
          <Modal.Title style={{ color: '#fa8c16', display: 'flex', alignItems: 'center' }}>
            <i className="fas fa-exclamation-triangle" style={{ marginRight: '8px', fontSize: '20px' }}></i>
            Document Already Exists
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {duplicateInfo && (
            <div>
              <div style={{ marginBottom: '20px', fontSize: '16px', lineHeight: '1.5' }}>
                A document with <strong>Haribo Item No "{duplicateInfo.haribo_item_no}"</strong> already exists in the system.
              </div>

              <div style={{ marginBottom: '20px', fontSize: '16px', fontWeight: '500', color: '#1890ff' }}>
                Do you want to overwrite the existing document with this new data?
              </div>

              <div style={{ marginBottom: '20px' }}>
                <strong>Existing Document Details:</strong>
                <div style={{ marginTop: '8px', padding: '12px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                  <div><strong>Job ID:</strong> {duplicateInfo.existing_job_id}</div>
                  <div style={{ marginTop: '4px' }}>
                    <strong>Created:</strong> {duplicateInfo.existing_timestamp ? new Date(duplicateInfo.existing_timestamp).toLocaleString() : 'Unknown'}
                  </div>
                </div>
              </div>

              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#fff7e6', borderRadius: '6px', border: '1px solid #ffd591' }}>
                <strong style={{ color: '#fa8c16' }}>⚠️ Important:</strong>
                <ul style={{ marginTop: '8px', marginBottom: '0', color: '#666' }}>
                  <li>Choosing "Overwrite" will completely replace the existing document</li>
                  <li>All previous data will be lost and cannot be recovered</li>
                  <li>Choosing "Keep Existing" will cancel this upload</li>
                </ul>
              </div>

              <div style={{ fontSize: '12px', color: '#666' }}>
                💡 Make sure you have the correct Haribo Item No before proceeding.
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setDuplicateModalVisible(false);
              setDuplicateInfo(null);
              setNewDataForReplacement(null);
            }}
            style={{ marginRight: '8px' }}
          >
            Keep Existing
          </Button>
          <Button
            variant="outline-primary"
            onClick={handleReplaceDuplicate}
          >
            Overwrite with New Data
          </Button>
        </Modal.Footer>
      </Modal>

      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </Container>
  );
};

export default DynamicForm;
